import type { CaptureResult, Properties } from 'posthog-js';

/**
 * Session replay masks all text by default (fail-closed). Add `ph-unmask` to a container to
 * reveal its text, and `ph-mask` inside it to hide a PII region again; the nearest marker wins.
 */
export const REPLAY_UNMASK_CLASS = 'ph-unmask';
export const REPLAY_MASK_CLASS = 'ph-mask';

/**
 * Elements replay replaces with an empty placeholder, on top of anything marked `ph-no-capture`.
 * Document viewers render whole investor documents (pdf.js draws page text as real DOM text),
 * and hidden inputs are the one input type `maskAllInputs` does not mask.
 */
export const REPLAY_BLOCK_SELECTOR = [
    'aix-pdf',
    'aix-pdf-viewer',
    'aix-image-viewer',
    'aix-signature-overlay',
    'aix-ui-document-viewer',
    'aix-document-viewer',
    'aix-order-form-preview',
    'input[type="hidden"]'
].join(', ');

// rrweb event types (https://github.com/rrweb-io/rrweb/blob/master/packages/types/src/index.ts)
const RRWEB_META_EVENT = 4;
const RRWEB_CUSTOM_EVENT = 5;

export function maskReplayText(text: string, element?: HTMLElement): string {
    const nearestMarker = element?.closest(`.${REPLAY_MASK_CLASS}, .${REPLAY_UNMASK_CLASS}`);
    if (nearestMarker?.classList.contains(REPLAY_UNMASK_CLASS)) {
        return text;
    }

    return text.replace(/\S/g, '*');
}

/**
 * Drops every query string and any non-route hash from a URL. The app uses hash routing, so a
 * route like `#/orders/123` is kept, but Auth0's implicit-flow callback lands on
 * `#access_token=...&id_token=...`, which must never leave the browser.
 */
export function scrubUrl(value: string): string {
    let url: URL;
    try {
        url = new URL(value);
    } catch {
        return value;
    }

    const route = url.hash.split('?')[0];
    url.search = '';
    url.hash = /^#\/[^=]*$/.test(route) ? route : '';
    url.username = '';
    url.password = '';
    return url.toString();
}

function scrubUrlProperties(properties: Properties | undefined): void {
    if (!properties) {
        return;
    }

    for (const [key, value] of Object.entries(properties)) {
        if (typeof value === 'string' && /^https?:\/\//i.test(value)) {
            properties[key] = scrubUrl(value);
        }
    }
}

function scrubSnapshotUrls(snapshots: unknown): void {
    if (!Array.isArray(snapshots)) {
        return;
    }

    for (const snapshot of snapshots) {
        if (snapshot?.type === RRWEB_META_EVENT && typeof snapshot.data?.href === 'string') {
            snapshot.data.href = scrubUrl(snapshot.data.href);
        }
        if (snapshot?.type === RRWEB_CUSTOM_EVENT) {
            scrubUrlProperties(snapshot.data?.payload);
        }
    }
}

/** `before_send` hook: scrubs URLs on analytics events and on session replay snapshots. */
export function scrubCapturedUrls(event: CaptureResult | null): CaptureResult | null {
    if (!event) {
        return event;
    }

    scrubUrlProperties(event.properties);
    scrubUrlProperties(event.properties?.['$set']);
    scrubUrlProperties(event.properties?.['$set_once']);
    scrubUrlProperties(event.$set);
    scrubUrlProperties(event.$set_once);
    scrubSnapshotUrls(event.properties?.['$snapshot_data']);
    return event;
}
